import React, { HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { useLocalStore, useObserver } from 'mobx-react-lite';
import { RandomStringGenerator, CharSet } from '../utils/randomString';
import styled from 'styled-components/macro';
import { useMst } from '../models/WithWineState';
import { TypeProduct } from '../types/TypeProduct';
import Button from './Button';
import { TryLikeBarProps } from '../types/TryLikeBarProps';
import Modal from './Modal';
import { ModalProps } from '../types/ModalProps';
import { isValidEmail } from '../utils/isValidEmail';
import Tickbox from './Tickbox';
import { allSettled } from '../utils/allSettled';

const randomStr = () => {
  const generator = new RandomStringGenerator(CharSet.ALPHANUMERIC);
  return generator.generate(6);
};

const UnstyledTryLikeBarSaveModal: React.FC<
  ModalProps & HTMLAttributes<HTMLDivElement>
> = (props) => {
  return useObserver(() => (
    <Modal
      {...props}
      className={`ww-widget ww-trylikebar-save-modal ${props.className ?? ''}`}
    />
  ));
};

const TryLikeBarSaveModal = styled(UnstyledTryLikeBarSaveModal)`
  .content .ww-trylikebar-save-content p {
    margin-top: 0;
    margin-bottom: 1em;
  }

  .content .ww-trylikebar-save-content {
    padding: 0.5em 1em;
    display: flex;
    flex-direction: column;
  }

  .content .ww-trylikebar-save-content .form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5em;

    input {
      margin-bottom: 0.25em;
    }

    span {
      font-weight: bold;
      margin-bottom: 0.25em;
    }
  }

  .content .ww-trylikebar-save-content .ww-tickbox {
    margin-top: 1em;
  }

  .content .ww-trylikebar-save-content .ww-tickbox .ww-field-error {
    margin-left: 1em;
  }

  .ww-trylikebar-save-content .ww-tickbox .symbol {
    border: 1px solid var(--gray-3);
  }

  .content .ww-trylikebar-save-content-success {
    text-align: center;
  }

  .content .ww-text-input {
    padding: 0.5em;
    background-color: var(--white);
    border: 0.071em solid var(--gray-2);
  }

  .content .ww-field-error {
    margin-top: 0.5em;
    color: var(--warn-color);
  }

  .content .ww-generic-error {
    margin: 1em 0;
    color: var(--warn-color);
  }

  .actions {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  .actions > * {
    flex: none;
    margin-right: 1em;
  }

  .actions > .delete-session {
    flex: auto;
    appearance: none;
    background-color: transparent;
    border: none;
    text-decoration: underline;
    cursor: pointer;
    text-align: left;
    outline: none;
  }

  .actions > .spacer {
    flex: auto;
  }

  .actions > *:last-child {
    margin-right: 0;
  }
`;

const UnstyledTryLikeBarDiscardModal: React.FC<
  ModalProps & HTMLAttributes<HTMLDivElement>
> = (props) => {
  return useObserver(() => (
    <Modal
      {...props}
      className={`ww-widget ww-trylikebar-discard-modal ${
        props.className ?? ''
      }`}
    />
  ));
};

const TryLikeBarDiscardModal = styled(UnstyledTryLikeBarDiscardModal)`
  .actions {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }

  .actions > * {
    flex: none;
    margin-right: 1em;
  }

  .actions > *:last-child {
    margin-right: 0;
  }

  .content {
    text-align: center;
  }
`;

const UnstyledTryLikeBar: React.FC<TryLikeBarProps> = ({
  className,
  returnToTastingURL,
}) => {
  const stateTree = useMst();
  const state = useLocalStore(() => ({
    lastRecord: '',
    busy: false,
    discardModalOpen: false,
    discardSuccess: false,
    saveModalOpen: false,
    saveSuccess: false,

    formDirty: false,
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    joinMailingList: true,
    agreeTerms: false,
    validEmail: false,
    validFirstName: false,
    validLastName: false,
    error: '',

    get isAuthenticated() {
      return stateTree.session?.authenticated ?? false;
    },

    get triedCount() {
      return Array.from(stateTree.data.products.values()).reduce(
        (count: number, product: TypeProduct) =>
          count + (product.isTasted ? 1 : 0),
        0,
      );
    },

    get likedCount() {
      return Array.from(stateTree.data.products.values()).reduce(
        (count: number, product: TypeProduct) =>
          count + (product.isLiked ? 1 : 0),
        0,
      );
    },

    get isHidden() {
      return (
        !(state.likedCount > 0 || state.triedCount > 0) ||
        state.lastRecord === state.likedCount + '|' + state.triedCount
      );
    },

    hide() {
      state.lastRecord = state.likedCount + '|' + state.triedCount;
    },

    onTick(name: string, value?: string | number | boolean) {
      if (name === 'joinMailingList') {
        state.joinMailingList = !value;
      } else if (name === 'agreeTerms') {
        state.agreeTerms = !value;
      }
    },

    setEmail(email: string) {
      state.email = email;
      state.validEmail = isValidEmail(email);
    },

    setFirstName(firstName: string) {
      state.firstName = firstName;
      state.validFirstName = !!firstName;
    },

    setLastName(lastName: string) {
      state.lastName = lastName;
      state.validLastName = !!lastName;
    },

    setPhoneNumber(phoneNumber: string) {
      state.phoneNumber = phoneNumber;
    },

    resetForm() {
      state.saveSuccess = false;
      state.error = '';
      state.formDirty = false;
      state.email = '';
      state.firstName = '';
      state.lastName = '';
      state.phoneNumber = '';
      state.joinMailingList = true;
      state.agreeTerms = false;
      state.validEmail = false;
      state.validFirstName = false;
      state.validLastName = false;
    },

    saveFormSubmit() {
      if (
        !state.validEmail ||
        !state.agreeTerms ||
        !state.validFirstName ||
        !state.validLastName
      ) {
        state.formDirty = true;
        return;
      }

      state.busy = true;
      stateTree.session
        ?.saveTastingSession(
          state.email,
          state.firstName,
          state.lastName,
          state.phoneNumber,
          state.joinMailingList,
        )
        .then(() => {
          state.resetForm();
          state.busy = false;
          state.saveSuccess = true;
        })
        .catch((error) => {
          state.resetForm();
          state.busy = false;
          state.error = error;
        });
    },

    deleteTastingSession() {
      state.busy = true;
      allSettled([
        Array.from(stateTree.data.products.values()).flatMap((product) => {
          return [
            product.isTasted ? product.onTried() : true,
            product.isLiked ? product.onLiked() : true,
          ];
        }),
      ]).then(() => {
        state.busy = false;
        state.discardSuccess = true;
      });
    },
  }));

  return useObserver(() =>
    createPortal(
      <div
        className={`ww-widget ww-trylikebar${
          state.isHidden ? ' ww-trylikebar-hide ' : ' '
        }${className ?? ''}`}
      >
        {!stateTree.session?.authenticated && (
          <div className="ww-trylikebar-spacer" />
        )}
        <div className="ww-trylikebar-label">
          <span>
            <i className="icon-ok" />
            <span>Tried: {state.triedCount}</span>
          </span>
          <span>
            <i className="icon-heart" />
            <span>Liked: {state.likedCount}</span>
          </span>
        </div>
        {stateTree.session?.authenticated ? (
          <div className="ww-trylikebar-user">
            <span className="ww-trylikebar-user-name">
              {stateTree.session?.user?.name}
            </span>
            <a
              href={`/user/logout?cb=${randomStr()}&from=${escape(
                window.location.href,
              )}`}
              className="ww-trylikebar-logout"
            >
              Logout
            </a>
          </div>
        ) : (
          <>
            <Button
              className="ww-trylikebar-save-tasting"
              secondary
              solid={true}
              onClick={() => {
                state.resetForm();
                state.saveModalOpen = true;
              }}
            >
              Save Tasting
            </Button>
          </>
        )}
        {stateTree.session?.authenticated && (
          <button
            className="ww-trylikebar-close-button"
            onClick={() => state.hide()}
          >
            ×
          </button>
        )}
        {!stateTree.session?.authenticated && (
          <div className="ww-trylikebar-spacer">
            {returnToTastingURL && (
              <a className="ww-trylikebar-action" href={returnToTastingURL}>
                Return to tasting
              </a>
            )}
            <button
              className="ww-trylikebar-action"
              onClick={() => (state.discardModalOpen = true)}
            >
              Discard
            </button>
          </div>
        )}
        <TryLikeBarDiscardModal
          open={state.discardModalOpen}
          onBackdropClick={() => {
            if (state.busy) {
              return;
            }
            state.discardSuccess = false;
            state.discardModalOpen = false;
          }}
          header={
            state.discardSuccess
              ? 'Thank you for tasting!'
              : 'You must have a good memory!'
          }
          footer={
            <div className="actions">
              <Button
                disabled={state.busy}
                onClick={() => {
                  state.discardModalOpen = false;
                  state.discardSuccess = false;
                }}
              >
                Go Back
              </Button>
              {!state.discardSuccess && (
                <Button
                  disabled={state.busy}
                  primary
                  solid
                  onClick={() => state.deleteTastingSession()}
                >
                  Delete
                </Button>
              )}
            </div>
          }
        >
          <p
            className={`content${
              state.discardSuccess
                ? ' ww-trylikebar-discard-content'
                : ' ww-trylikebar-discard-content-success'
            }`}
          >
            {state.discardSuccess
              ? 'We look forward to seeing you again!'
              : 'Are you sure you want to delete your tries and likes?'}
          </p>
        </TryLikeBarDiscardModal>
        <TryLikeBarSaveModal
          open={state.saveModalOpen}
          onBackdropClick={() => {
            if (state.busy) {
              return;
            }
            state.saveSuccess = false;
            state.saveModalOpen = false;
          }}
          header={
            state.saveSuccess ? 'Thank you for tasting!' : 'Save your tastings'
          }
          footer={
            <div className="actions">
              {!state.saveSuccess && (
                <button
                  className="delete-session"
                  onClick={() => {
                    state.discardModalOpen = true;
                    state.saveModalOpen = false;
                    state.saveSuccess = false;
                  }}
                >
                  No, thanks. Delete my session.
                </button>
              )}
              {state.saveSuccess && <div className="spacer" />}
              <Button
                disabled={state.busy}
                onClick={() => {
                  state.saveModalOpen = false;
                  state.saveSuccess = false;
                }}
              >
                Go Back
              </Button>
              {!state.saveSuccess && (
                <Button
                  disabled={state.busy}
                  primary
                  solid
                  onClick={state.saveFormSubmit}
                >
                  Save
                </Button>
              )}
            </div>
          }
        >
          <div className="content">
            {!state.saveSuccess ? (
              <div className="ww-trylikebar-save-content">
                <p>
                  Please enter your details to create a <b>WithWine account</b>{' '}
                  and save your tastings.
                </p>
                <label className="form-field">
                  <span>Email</span>
                  <input
                    autoFocus
                    type="email"
                    placeholder="Email"
                    className="ww-text-input"
                    value={state.email}
                    disabled={state.busy}
                    onChange={(e) => state.setEmail(e.target.value)}
                  />
                  {!state.validEmail && state.formDirty && (
                    <small className="ww-field-error">
                      Your email is not valid.
                    </small>
                  )}
                </label>
                <label className="form-field">
                  <span>First Name</span>
                  <input
                    type="text"
                    placeholder="First Name"
                    className="ww-text-input"
                    value={state.firstName}
                    disabled={state.busy}
                    onChange={(e) => state.setFirstName(e.target.value)}
                  />
                  {!state.validFirstName && state.formDirty && (
                    <small className="ww-field-error">
                      First Name is required.
                    </small>
                  )}
                </label>
                <label className="form-field">
                  <span>Last Name</span>
                  <input
                    type="text"
                    placeholder="Last Name"
                    className="ww-text-input"
                    value={state.lastName}
                    disabled={state.busy}
                    onChange={(e) => state.setLastName(e.target.value)}
                  />
                  {!state.validLastName && state.formDirty && (
                    <small className="ww-field-error">
                      Last Name is required.
                    </small>
                  )}
                </label>
                <label className="form-field">
                  <span>Phone Number (optional)</span>
                  <input
                    type="text"
                    placeholder="Phone Number"
                    className="ww-text-input"
                    value={state.phoneNumber}
                    disabled={state.busy}
                    onChange={(e) => state.setPhoneNumber(e.target.value)}
                  />
                </label>

                <Tickbox
                  name="joinMailingList"
                  active={state.joinMailingList}
                  value={state.joinMailingList}
                  onTick={state.onTick}
                >
                  Join the newsletter
                </Tickbox>
                <Tickbox
                  name="agreeTerms"
                  active={state.agreeTerms}
                  value={state.agreeTerms}
                  onTick={state.onTick}
                >
                  I agree to{' '}
                  <a
                    href="https://www.withwine.com/terms-of-use/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WithWine Terms of Use
                  </a>
                  {!state.agreeTerms && state.formDirty && (
                    <small className="ww-field-error">
                      You must accept the terms of use.
                    </small>
                  )}
                </Tickbox>
                {state.error && (
                  <div className="ww-generic-error">{state.error}</div>
                )}
              </div>
            ) : (
              <div className="ww-trylikebar-save-content-success">
                <p>
                  To view the wines you&apos;ve tasted, follow the instructions
                  that have been sent to your email.
                </p>
                <p>We look forward to seeing you again!</p>
              </div>
            )}
          </div>
        </TryLikeBarSaveModal>
      </div>,
      document.getElementsByTagName('body')[0],
    ),
  );
};

const TryLikeBar = styled(UnstyledTryLikeBar)`
  background-color: var(--primary-color);
  color: var(--white);

  position: sticky;
  z-index: 9000;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-height: 3.538em;
  padding: 0 2em;

  &.ww-trylikebar-hide {
    display: none;
  }

  .ww-trylikebar-spacer {
    display: flex;
    align-items: center;
    flex: auto;
    justify-content: center;
    flex: 1 1;
  }

  .ww-trylikebar-label {
    display: flex;
    align-items: center;

    & > span {
      display: flex;
      align-items: center;
      text-transform: uppercase;
      padding: 1em;

      & > span {
        margin-left: 0.5em;
        font-size: 1.077em;
        font-weight: 600;
        text-align: center;
      }
    }
  }

  .ww-trylikebar-user {
    display: flex;
    align-items: center;

    .ww-trylikebar-user-name {
      padding: 1em;
    }

    .ww-trylikebar-logout {
      color: var(--white);
      text-decoration: underline;
    }
  }

  .ww-trylikebar-save-tasting {
    margin: 0.35em 1em;
    text-transform: uppercase;
  }

  .ww-trylikebar-close-button {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    height: 2.25em;
    width: 1.5em;
    color: var(--white);
    background-color: transparent;
    outline: none;
    border: none;
    appearance: none;
    cursor: pointer;
    font-size: 1.615em;
    font-weight: 600;
  }

  .ww-trylikebar-action {
    height: 3.538em;
    line-height: 3.538em;
    appearance: none;
    border: none;
    background-color: transparent;
    padding: 0 1em;
    color: var(--white);
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
  }

  @media (max-width: 960px) {
    .ww-trylikebar-spacer {
      flex: none;
    }
  }
`;

export default TryLikeBar;
